/** Fleet integration → Vehicles tab: fleet-config-scoped actions + a deep-link.
 *
 * The vehicle roster + map now live on the top-level /vehicles page (cross-tenant,
 * standard grid). This tab keeps the actions that are genuinely per-fleet-config —
 * Sync vehicles / Poll locations — and hands off roster management to that page,
 * pre-filtered to this operator.
 */
import MyLocationIcon from '@mui/icons-material/MyLocation';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SyncIcon from '@mui/icons-material/Sync';
import { Button, Stack, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';

import { ConsoleApiError } from '@/api/client';
import * as vehiclesApi from '@/api/vehicles';
import { useSnackbar } from '@/components/Snackbar';

interface Props {
  toId: number;
  fleetId: number;
  canManage: boolean;
}

function summarize(stats: Record<string, unknown>): string {
  const parts = Object.entries(stats)
    .filter(([, v]) => typeof v === 'number')
    .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`);
  return parts.join(', ') || 'done';
}

export function FleetVehiclesActions({ toId, fleetId, canManage }: Props) {
  const snackbar = useSnackbar();
  const onError = (e: unknown, fallback: string) =>
    snackbar.show(e instanceof ConsoleApiError ? e.message : fallback, 'error');

  const syncMutation = useMutation({
    mutationFn: () => vehiclesApi.syncVehicles(toId, fleetId),
    onSuccess: (r) => snackbar.show(`Sync complete — ${summarize(r.stats)}`, 'success'),
    onError: (e) => onError(e, 'Vehicle sync failed.'),
  });
  const pollMutation = useMutation({
    mutationFn: () => vehiclesApi.pollLocations(toId, fleetId),
    onSuccess: (r) => snackbar.show(`Locations polled — ${summarize(r.stats)}`, 'success'),
    onError: (e) => onError(e, 'Location poll failed.'),
  });

  return (
    <Stack spacing={2} sx={{ py: 1 }}>
      <Typography variant="body2" color="text.secondary">
        Manage the vehicle roster and live map for this operator on the Vehicles page. Sync
        and location polling stay here — they run against this specific fleet configuration.
      </Typography>
      <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap' }}>
        {canManage && (
          <>
            <Button
              variant="outlined" size="small" startIcon={<SyncIcon />}
              disabled={syncMutation.isPending} onClick={() => syncMutation.mutate()}
            >
              {syncMutation.isPending ? 'Syncing…' : 'Sync vehicles'}
            </Button>
            <Button
              variant="outlined" size="small" startIcon={<MyLocationIcon />}
              disabled={pollMutation.isPending} onClick={() => pollMutation.mutate()}
            >
              {pollMutation.isPending ? 'Polling…' : 'Poll locations'}
            </Button>
          </>
        )}
        <Button
          variant="contained" size="small" endIcon={<OpenInNewIcon />}
          component={RouterLink} to={`/vehicles?tenant=${toId}&view=list`}
        >
          Manage vehicles
        </Button>
      </Stack>
    </Stack>
  );
}
